/* eslint-disable no-param-reassign */
import {
  reactive, ref, computed,
} from '@vue/composition-api'
import { successNotification, errorNotification } from '@/utils/toast-notifications'

export default function useVersionForm(props, ctx) {
  const rateOptions = [
    {
      text: 'Percentage',
      value: 'percentage',
    },
    {
      text: 'Fixed',
      value: 'fixed',
    },
  ]
  const versionFormRef = ref(null)
  const isPublished = ref(false)
  const responseVersion = ref({})
  const version = ref({
    label: '',
    slug: '',
    published: false,
    effective_date_time: new Date(),
    zone: ctx.root.$route.query.zone,
    service_class: props.lastVersion.service_class,
    settings: {
      minimum_amount: null,
      tax_amount: null,
      open_meter_amount: null,
      admin_commission: null,
      delay_discount_message: {
        en: '',
        ar: '',
      },
      delay_discount_enabled: false,
      estimates: {
        rate: 'percentage',
        value: null,
        value_upper: null,
      },
      arrival: {
        amount_per_minute: null,
        free_waiting_time: null,
        maximum_time_threshold: null,
      },
      journey_wait_time: [],
      upfront_estimates: {
        active: false,
        minimum_distance_threshold: null,
        distance: {
          rate: 'percentage',
          minimum: null,
          maximum: null,
        },
        time: {
          rate: 'percentage',
          minimum: null,
          maximum: null,
        },
      },
      time_frame_slices: [],
      drop_zones: {},
      peak_factors: [],
      time_slicings: [],
      distance_slicings: [],
      journey_wait_time: [],
      session_discount_slices: [],
    },
  })

  const convertUTCToLocalTime = time => {
    // Define the UTC time string
    const utcTimeString = time

    // Create a Date object using today's date and the given UTC time string
    const today = new Date()
    const [utcHours, utcMinutes, utcSeconds] = utcTimeString.split(':').map(Number)

    // Create a new Date object with the given UTC time
    const utcDate = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate(), utcHours, utcMinutes, utcSeconds))

    // Get the local equivalent of the UTC time
    const localHours = utcDate.getHours().toString().padStart(2, '0')
    const localMinutes = utcDate.getMinutes().toString().padStart(2, '0')
    const localSeconds = utcDate.getSeconds().toString().padStart(2, '0')

    const localTimeString = `${localHours}:${localMinutes}:${localSeconds}`

    return localTimeString
  }

  const distanceLabeling = computed(() => (version.value.settings.upfront_estimates.distance?.rate === 'percentage' ? '(%)' : '(KM)'))
  const timeLabeling = computed(() => (version.value.settings.upfront_estimates.time?.rate === 'percentage' ? '(%)' : '(Minute)'))

  const effectiveDate = ref(null)
  const effectiveTime = ref(null)

  
  version.value = JSON.parse(JSON.stringify(props.lastVersion))
  responseVersion.value = JSON.parse(JSON.stringify(props.lastVersion))

  
  if (version.value.settings.time_frame_slices && version.value.settings.time_frame_slices.length) {
    version.value.settings.time_frame_slices.forEach(s => {
      s.start_time = convertUTCToLocalTime(s.start_time)
      s.end_time = convertUTCToLocalTime(s.end_time)
    })
  }

  
  if (version.value.settings?.upfront_estimates && Object.keys(version.value.settings?.upfront_estimates).length) {
    if (version.value.settings?.upfront_estimates?.time?.rate === 'fixed') {
      version.value.settings.upfront_estimates.time.maximum /= 60
      version.value.settings.upfront_estimates.time.minimum /= 60
    }
  }
  if (!('upfront_estimates' in version.value.settings)) {
    version.value.settings.upfront_estimates = reactive({
      active: false,
      minimum_distance_threshold: null,
      distance: {
        rate: 'percentage',
        minimum: null,
        maximum: null,
      },
      time: {
        rate: 'percentage',
        minimum: null,
        maximum: null,
      },
    })
  } else {
    if (version.value.settings.upfront_estimates.distance === null) {
      version.value.settings.upfront_estimates.distance = {
        rate: 'percentage',
        minimum: null,
        maximum: null,
      }
    }
    if (version.value.settings.upfront_estimates.time === null) {
      version.value.settings.upfront_estimates.time = {
        rate: 'percentage',
        minimum: null,
        maximum: null,
      }
    }
  }

  // Ensure journey_wait_time exists
  if (!('journey_wait_time' in version.value.settings)) {
    version.value.settings.journey_wait_time = []
  }

  isPublished.value = false
  version.value.published = false
  if (props.isNew) {
    version.value.effective_date_time = null
  } else {
    version.value.effective_date_time = new Date(`${props.lastVersion.effective_date_time}`)
  }

  const convertTimeToUTC = time => {
    const today = new Date()
    const temp = new Date(`${today.getFullYear()}-${today.getMonth() + 1}-${today.getDate()} ${time}`)
    const timeSplit = temp.toISOString().split('T')[1]
    const fullTimeValue = timeSplit.split('.')[0]
    return fullTimeValue
  }
  const submitting = ref(false)

  const openPublishModal = async () => {
    const valid = await versionFormRef.value.validate()
    if (!valid) {
      errorNotification('Validation Failed..')
      return
    }
    ctx.root.$bvModal.show(`publish-version-${props.lastVersion.service_class}`)
  }

  const submitVersion = async () => {
    const valid = await versionFormRef.value.validate()
    if (!valid) return
    try {
      const temp = JSON.parse(JSON.stringify(version.value))
      if (!temp.settings.upfront_estimates.active) {
        if (!temp.settings.upfront_estimates.minimum_distance_threshold) {
          temp.settings.upfront_estimates.minimum_distance_threshold = null
        }
        // Backend responds with data as null
        if (responseVersion.value.upfront_estimates?.time === null && !temp.settings.upfront_estimates.time.maximum && !temp.settings.upfront_estimates.time.minimum) {
          temp.settings.upfront_estimates.time = null
        }
        if (responseVersion.value.upfront_estimates?.distance === null && !temp.settings.upfront_estimates.distance.maximum && !temp.settings.upfront_estimates.distance.minimum) {
          temp.settings.upfront_estimates.distance = null
        }
        // Backend responds with valid data and the admin clears it
        if (!temp.settings.upfront_estimates.time.maximum && !temp.settings.upfront_estimates.time.minimum) {
          temp.settings.upfront_estimates.time = null
        }
        if (!temp.settings.upfront_estimates.distance.maximum && !temp.settings.upfront_estimates.distance.minimum) {
          temp.settings.upfront_estimates.distance = null
        }
      }

      // Convert to seconds
      if (temp.settings.upfront_estimates?.time?.rate === 'fixed') {
        temp.settings.upfront_estimates.time.maximum *= 60
        temp.settings.upfront_estimates.time.minimum *= 60
      }
      temp.effective_date_time = new Date(version.value.effective_date_time).toISOString()
      // convertTime()

      if (temp.settings.time_frame_slices && temp.settings.time_frame_slices.length) {
        let hasErrors = false
        temp.settings.time_frame_slices.forEach((s, ix) => {
          if (!s.time_slicings.length && !s.distance_slicings.length) {
            hasErrors = true
            errorNotification(`Must include Time OR Distance slicings in Frame #${ix + 1}`)
          }
          s.start_time = convertTimeToUTC(s.start_time)
          s.end_time = convertTimeToUTC(s.end_time)
        })
        if (hasErrors) return
      }

      submitting.value = true
      const getNextVersion = await ctx.root.$store.dispatch('versions/getVersion', {
        zone: ctx.root.$route.query.zone,
        service: props.lastVersion.service_class,
      })
      const { label, slug } = getNextVersion.data.data
      temp.label = label
      temp.slug = slug
      temp.zone = ctx.root.$route.query.zone
      console.log(temp, 'te')
      const resp = await ctx.root.$store.dispatch('versions/createVersion', temp)
      const id = resp?.data?.data?.id
      await ctx.root.$store.dispatch('versions/editVersion', { id, version: { ...temp, published: true } })
      ctx.root.$bvModal.hide(`publish-version-${props.lastVersion.service_class}`)
      successNotification('Version created successfully')
      ctx.emit('next-tab')
      submitting.value = false
    } catch (error) {
      console.log(error)
      const errorMsg = error?.response?.data?.error?.detail?.error
      let message = error?.response?.data?.error?.detail?.message
      if (errorMsg && errorMsg.includes('ix_pricing_versions_slug')) {
        message = 'The version slug already exists'
      }
      if (errorMsg && errorMsg.includes('ix_pricing_versions_effective_date_time')) {
        message = 'Effective Date Time already exists'
      }
      errorNotification(message || 'Failed')
      console.error(errorMsg || error.message)
    } finally {
      // convertTime('GMT', 'Asia/Amman')
      submitting.value = false
    }
  }

  return {
    submitVersion,
    openPublishModal,
    // fetchingZones,
    // zones,
    // zonesNames,
    submitting,
    rateOptions,
    version,
    effectiveDate,
    effectiveTime,
    versionFormRef,
    // fetchingVersion,
    isPublished,
    distanceLabeling,
    timeLabeling,
  }
}
